import { z } from "zod";
export const PLANK_LASER_REPEAT_TOLERANCE_MM = 10;
export const PLANK_MIN_REUSABLE_OFFCUT_MM = 300;
export const StraightPlankInstallInput = z.object({
    schema: z.literal("artemis.straight-plank-install-input/1"),
    work_id: z.string().uuid(),
    assessment_revision_id: z.string().uuid(),
    product: z.object({ plank_length_mm: z.number().finite().positive(), plank_width_mm: z.number().finite().positive().nullable().default(null) }),
    rows: z.array(z.object({
        id: z.string().min(1), label: z.string().min(1), readingsMM: z.array(z.number().finite().positive()).max(20),
        startTransitionInsetMM: z.number().finite().nullable().optional(), endTransitionInsetMM: z.number().finite().nullable().optional(),
        status: z.enum(["planned", "done", "revise", "failed"]).optional(),
    })).max(1000),
    wall_gap_mm: z.number().finite().min(0).max(50).default(5),
    site_allows_precut: z.boolean().default(true),
});
export const StraightPlankInstallOutput = z.object({
    schema: z.literal("artemis.straight-plank-install-output/1"),
    work_id: z.string().uuid(),
    assessment_revision_id: z.string().uuid(),
    cuts: z.array(z.object({ rowId: z.string(), rowLabel: z.string(), sequence: z.number().int().positive(), lengthMM: z.number().finite().positive(), source: z.enum(["new_board", "reused_offcut", "full_board"]), sourceId: z.number().int().positive() })),
    offcutsMM: z.array(z.number().finite().nonnegative()),
    suitability: z.enum(["ready_to_precut", "verify_on_site", "site_cut_only"]),
    reasons: z.array(z.string()), measuredRows: z.number().int().nonnegative(), verifiedRows: z.number().int().nonnegative(), wallGapMM: z.number().finite().nonnegative(),
});
const stagger = (row) => (row * 0.6180339887498949) % 1;
const median = (values) => values.length % 2 ? values[(values.length - 1) / 2] : (values[values.length / 2 - 1] + values[values.length / 2]) / 2;
const safeFirstCut = (desired, total, plankLengthMM) => {
    const end = (total - desired) % plankLengthMM;
    if (end < 0.5 || end >= PLANK_MIN_REUSABLE_OFFCUT_MM)
        return desired;
    const finishOnFullBoard = desired + end;
    const leaveMinimumEnd = desired - (PLANK_MIN_REUSABLE_OFFCUT_MM - end);
    if (finishOnFullBoard <= plankLengthMM)
        return finishOnFullBoard;
    return Math.max(PLANK_MIN_REUSABLE_OFFCUT_MM, leaveMinimumEnd);
};
/** Length-only pre-cut schedule. Width fitting remains an installer task. */
export function planPlankCuts(rows, plankLengthMM, options = {}) {
    const wallGapMM = options.wallGapMM ?? 5;
    const siteAllowsPrecut = options.siteAllowsPrecut ?? true;
    if (!(plankLengthMM > 0) || !Number.isFinite(plankLengthMM))
        return { cuts: [], offcutsMM: [], suitability: "site_cut_only", reasons: ["Choose a product with a fixed plank length."], measuredRows: rows.length, verifiedRows: 0, wallGapMM };
    const cuts = [];
    const offcuts = [];
    const reasons = [];
    let nextBoard = 1;
    let verifiedRows = 0;
    rows.forEach((row, rowIndex) => {
        const startInset = row.startTransitionInsetMM ?? 0;
        const endInset = row.endTransitionInsetMM ?? 0;
        if (![startInset, endInset].every(n => Number.isFinite(n))) {
            reasons.push(`${row.label}: measure the doorway transition offset again.`);
            return;
        }
        const valid = row.readingsMM.filter((value) => Number.isFinite(value) && value > wallGapMM * 2).sort((a, b) => a - b);
        if (!valid.length) {
            reasons.push(`${row.label}: no laser length.`);
            return;
        }
        const adjustedLength = median(valid) + startInset + endInset - wallGapMM * 2;
        if (!Number.isFinite(adjustedLength) || adjustedLength <= 0) {
            reasons.push(`${row.label}: transition adjustment leaves no usable floor run.`);
            return;
        }
        const spread = valid.at(-1) - valid[0];
        if (valid.length < 2)
            reasons.push(`${row.label}: take a second laser shot before pre-cutting.`);
        else if (spread > PLANK_LASER_REPEAT_TOLERANCE_MM)
            reasons.push(`${row.label}: repeated shots differ by ${Math.round(spread)} mm; measure again.`);
        else
            verifiedRows += 1;
        let remaining = adjustedLength;
        let sequence = 1;
        if (remaining > PLANK_MIN_REUSABLE_OFFCUT_MM * 2) {
            const suitable = offcuts.map((piece, index) => ({ ...piece, index })).filter((piece) => {
                const end = (remaining - piece.mm) % plankLengthMM;
                return piece.mm >= PLANK_MIN_REUSABLE_OFFCUT_MM && piece.mm <= remaining - PLANK_MIN_REUSABLE_OFFCUT_MM && (end < 0.5 || end >= PLANK_MIN_REUSABLE_OFFCUT_MM);
            }).sort((a, b) => a.mm - b.mm)[0];
            if (suitable) {
                offcuts.splice(suitable.index, 1);
                cuts.push({ rowId: row.id, rowLabel: row.label, sequence: sequence++, lengthMM: suitable.mm, source: "reused_offcut", sourceId: suitable.id });
                remaining -= suitable.mm;
            }
        }
        if (sequence === 1 && remaining > plankLengthMM + PLANK_MIN_REUSABLE_OFFCUT_MM) {
            const looseStack = Math.max(PLANK_MIN_REUSABLE_OFFCUT_MM, Math.min(plankLengthMM, plankLengthMM * Math.max(0.25, stagger(rowIndex + 1))));
            const desired = safeFirstCut(looseStack, remaining, plankLengthMM);
            const boardId = nextBoard++;
            cuts.push({ rowId: row.id, rowLabel: row.label, sequence: sequence++, lengthMM: desired, source: "new_board", sourceId: boardId });
            if (plankLengthMM - desired >= PLANK_MIN_REUSABLE_OFFCUT_MM)
                offcuts.push({ id: boardId, mm: plankLengthMM - desired });
            remaining -= desired;
        }
        while (remaining > plankLengthMM + 0.5) {
            const boardId = nextBoard++;
            cuts.push({ rowId: row.id, rowLabel: row.label, sequence: sequence++, lengthMM: plankLengthMM, source: "full_board", sourceId: boardId });
            remaining -= plankLengthMM;
        }
        if (remaining > 0.5) {
            const boardId = nextBoard++;
            const source = Math.abs(remaining - plankLengthMM) < 0.5 ? "full_board" : "new_board";
            cuts.push({ rowId: row.id, rowLabel: row.label, sequence, lengthMM: remaining, source, sourceId: boardId });
            if (plankLengthMM - remaining >= PLANK_MIN_REUSABLE_OFFCUT_MM)
                offcuts.push({ id: boardId, mm: plankLengthMM - remaining });
        }
    });
    if (!siteAllowsPrecut)
        reasons.unshift("This site is marked site-cut only.");
    const suitability = !siteAllowsPrecut || !rows.length ? "site_cut_only" : verifiedRows === rows.length ? "ready_to_precut" : "verify_on_site";
    return { cuts, offcutsMM: offcuts.map((piece) => piece.mm).sort((a, b) => b - a), suitability, reasons, measuredRows: rows.length, verifiedRows, wallGapMM };
}
/** Straight-plank only. Herringbone remains a separate handed-board contract. */
export function planStraightPlankInstall(raw) {
    const input = StraightPlankInstallInput.parse(raw);
    const plan = planPlankCuts(input.rows, input.product.plank_length_mm, { wallGapMM: input.wall_gap_mm, siteAllowsPrecut: input.site_allows_precut });
    return StraightPlankInstallOutput.parse({ schema: "artemis.straight-plank-install-output/1", work_id: input.work_id, assessment_revision_id: input.assessment_revision_id, ...plan });
}
//# sourceMappingURL=plank-cut-runner.js.map